import * as React from 'react'
import { FormattedMessage, FormattedHTMLMessage, useIntl } from 'react-intl'
import isMobile from 'ismobilejs'
import GenericStep from '../generic-step'
import IbSessionStatus from '../../../../components/ib-session-status'
import { ReactComponent as BankIdSvgWhite } from '../../../../components/functional/basic-signup/form/bankid-white.svg'
import swedbankLogo from './swedbank.png'
import sebLogo from './seb.png'
import handelsbankenLogo from './handelsbanken.png'
import skvLogo from './skatteverket.png'
import styles from './step-3.module.scss'

import BankidCard from '../../../../components/bankid-card'
import Progress from '../step-2/progress'
import WhatYouGet from '../what-you-get'
import { AccountingOnboardingSyncedPreOfferStatus } from '../../../../__generated__/globalTypes'
import { ICompanyProps } from '../../../signup-v3/dooerPreOffer'
import { noop } from 'lodash-es'

interface IStep3Props {
  status: AccountingOnboardingSyncedPreOfferStatus
  bank: string
  company: ICompanyProps
  bankIdSessionId: string
  onCancel: () => void
}

const Step3 = ({ status: progress, bank, company, bankIdSessionId, onCancel }: IStep3Props) => {
  // Use intl HOC
  const intl = useIntl()

  const banks: {
    [bank: string]: {
      logo: string
      logoAlt: string
    }
  } = {
    swedbankCompany: {
      logo: swedbankLogo,
      logoAlt: 'Swedbank logotype',
    },
    sebCompany: {
      logo: sebLogo,
      logoAlt: 'SEB logotype',
    },
    handelsbankenCompany: {
      logo: handelsbankenLogo,
      logoAlt: 'handelsbanken logotype',
    },
  }

  const renderBrand = () => {
    if (
      [
        AccountingOnboardingSyncedPreOfferStatus.pendingOrganizationSummarySync,
        AccountingOnboardingSyncedPreOfferStatus.organizationSummarySynced,
      ].includes(progress)
    ) {
      return {
        logo: skvLogo,
        logoAlt: 'Skatteverket logotype',
        message: {
          defaultMessage: 'Nu kommer vi att logga in på Skatteverket för att lära oss mer om ditt företag',
          id: 'sync-bank.step-3.brands.skv.message',
        },
      }
    } else if (['pendingBankSync', 'bankSynced', 'fetchAccountsDone', 'done'].includes(progress)) {
      return {
        ...banks[bank],
        message: {
          defaultMessage: 'Nu kommer vi att logga in på din bank för att se hur många transaktioner du gör per månad',
          id: 'sync-bank.step-3.brands.bank.message',
        },
      }
    }
  }

  const institutions: { [i: string]: string } = {
    bankgiro: 'bankgiro',
    handelsbankenCompany: 'Handelsbanken',
    manual: 'manual',
    nordea: 'Nordea',
    paypal: 'Paypal',
    sebCompany: 'SEB',
    skatteverket: 'Skatteverket',
    swedbank: 'Swedbank',
    mockSkatteverket: 'Skatteverket',
    mockBank: 'banken',
    swedbankCompany: 'Swedbank',
    swedbankEntercardCompany: 'Swedbank Entercard',
    swedbankPersonal: 'Swedbank Personlig',
  }

  const renderStatusMessage = (
    status: string | null,
    institution: string | null,
    bankIdQRImage: string | null | undefined,
    autoStartToken: string | null | undefined
  ) => {
    const statusEnums: { [e: string]: string } = {
      pending: intl.formatMessage({
        defaultMessage: 'Var vänlig och vänta några sekunder medan vi förbereder saker...',
        id: 'sync-bank.step-3.status-pending',
      }),
      processing: intl.formatMessage(
        {
          defaultMessage: 'Hämtar data{institution} för att analysera företaget',
          id: 'sync-bank.step-3.status-processing',
        },
        { institution: institution ? ` ifrån ${institutions[institution]}` : '' }
      ),
      failed: intl.formatMessage({
        defaultMessage: 'Tyvärr, ett fel har skett och processen kommer inte fortsätta...',
        id: 'sync-bank.step-3.status-failed',
      }),
      done: intl.formatMessage(
        {
          defaultMessage: 'Hämtade den nödvändiga informationen{institution}',
          id: 'sync-bank.step-3.status-done',
        },
        { institution: institution ? ` ifrån ${institutions[institution]}` : '' }
      ),
      waitingForBankid: intl.formatMessage({
        defaultMessage: 'Var vänlig öppna BankID appen på din mobil eller surfplatta...',
        id: 'sync-bank.step-3.status-waiting-for-bankid',
      }),
      partiallyFailed: intl.formatMessage({
        defaultMessage: 'Ett fel har av någon anledning uppstått under sessionen...',
        id: 'sync-bank.step-3.status-partiallyFailed',
      }),
    }

    if (status === 'waitingForBankid') {
      return (
        <>
          <div style={{ display: 'flex' }}>
            <BankIdSvgWhite />
            {statusEnums[status]}
          </div>
          {(isMobile().phone || isMobile().tablet) && (
            <div>
              <a
                href={`bankid:///?autostarttoken=${autoStartToken || ''}&redirect=${encodeURI(window.location.href)}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                <FormattedMessage
                  defaultMessage="Klicka här för att öppna BankID på den här enheten"
                  id="sync-bank.step-3.open-bankid"
                />
              </a>
            </div>
          )}
          {bankIdQRImage && (
            <div style={{ display: 'flex', width: '100%', justifyContent: 'center', marginTop: '1rem' }}>
              <img alt="QR" src={bankIdQRImage} width="70%" />
            </div>
          )}
        </>
      )
    }

    return statusEnums[status || '']
  }

  return (
    <GenericStep
      company={company}
      content={
        <>
          <IbSessionStatus onDone={noop} sessionId={bankIdSessionId}>
            {({ session }) => (
              <div>
                <BankidCard
                  brand={renderBrand()}
                  cancelMessage={{
                    defaultMessage: 'Jag vill inte synka, kontakta mig istället',
                    id: 'sync-bank.step-3.cancel',
                  }}
                  message={session.message}
                  onCancel={onCancel}
                  skipBankidLogo
                  status={
                    session
                      ? renderStatusMessage(
                          session.status,
                          session.institution,
                          session.bankIdQRImage,
                          session.autoStartToken
                        )
                      : null
                  }
                  waitingForBankid={session.status === 'waitingForBankid'}
                />
                <p className={styles.gdpr}>
                  <FormattedHTMLMessage
                    defaultMessage="Det är helt säkert. Läs vår <a target='_blank' rel='noopener noreferrer' href='/gdpr'>integritetspolicy</a> om du är osäker."
                    id="sync-bank.step-3.gdpr"
                  />
                </p>
              </div>
            )}
          </IbSessionStatus>

          <Progress status={progress} />
        </>
      }
      footer={<WhatYouGet />}
      header={
        <>
          <FormattedMessage defaultMessage="Analys pågår …" id="sync-bank.step-3.title-bank" tagName="h1" />
          <FormattedMessage
            defaultMessage="Just nu hämtar vi data från Skatteverket och din bank för att kunna göra en rättvis bedömning av ditt företag och ta fram ett pris som utgår ifrån din verksamhet."
            id="sync-bank.step-3.lede"
            tagName="p"
          />
        </>
      }
    />
  )
}

export default Step3
